import "./TiptapEditor.scss";

import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import ListItem from "@tiptap/extension-list-item";
import Placeholder from "@tiptap/extension-placeholder";
import { isEqual } from "lodash";
import Link from "@tiptap/extension-link";

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div className="control-group bg-light">
      <div className="button-group">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "is-active" : ""}
          title="Bold"
        >
          <i className="fa fa-bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "is-active" : ""}
          title="Italic"
        >
          <i className="fa fa-italic" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "is-active" : ""}
          title="Bullet List"
        >
          <i className="fa fa-list-ul" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive("orderedList") ? "is-active" : ""}
          title="Ordered List"
        >
          <i className="fa fa-list-ol" />
        </button>

        <button
          onClick={() => {
            const url = window.prompt("Enter URL");
            if (url) {
              editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: url })
                .run();
            }
          }}
          className={editor.isActive("link") ? "is-active" : ""}
          title="Insert Link"
        >
          <i className="fa fa-link" />
        </button>
      </div>
    </div>
  );
};

const TiptapEditor = ({ value = "", onChange, onEnter, placeholder, enableToolbar }) => {
  const editor = useEditor({
    extensions: [
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      TextStyle.configure({ types: [ListItem.name] }),
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || "Start typing...",
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
      }),
    ],
    content: value?.json || value || "",
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const text = editor.getText();
      onChange?.({ json, text });
    },
    editorProps: {
      handleKeyDown(view, event) {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          typeof onEnter === "function" && onEnter();
          return true;
        }
        return false;
      },
    },
  });

  // Sync external value into the editor if changed
  useEffect(() => {
    if (!editor) return;
  
    if (value?.json) {
      // When value is structured JSON, sync if changed
      if (!isEqual(value.json, editor.getJSON())) {
        editor.commands.setContent(value.json, false);
      }
    } else if (value === "" || value === null) {
      // Handle reset
      editor.commands.setContent("", false);
    }
  }, [value, editor]);

  return (
    <div className="message-editor">
      {enableToolbar && <MenuBar editor={editor} />}
      <EditorContent
        editor={editor}
        className="tiptap"
        placeholder="Jot down"
      />
    </div>
  );
};

export default TiptapEditor;
